import { useState, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Кэш для blob URL чтобы не загружать повторно
const blobUrlCache = new Map<string, string>();

// Глобальный колбэк для уведомления о необходимости переавторизации Telegram
let onSessionExpiredCallback: (() => void) | null = null;

// Флаг что уже вызвали колбэк (чтобы не спамить)
let sessionExpiredNotified = false;

/**
 * Установить колбэк для обработки истечения Telegram сессии
 * Вызывается один раз при монтировании TelegramClientPage
 *
 * ВАЖНО: Это для TELEGRAM сессии (MTProto), а не для JWT.
 * JWT авторизация обрабатывается глобальным интерцептором в fetch-with-auth.ts
 */
export function setOnSessionExpired(callback: (() => void) | null): void {
  onSessionExpiredCallback = callback;
  // Сбрасываем флаг при установке нового колбэка
  if (callback) {
    sessionExpiredNotified = false;
  }
}

/**
 * Уведомить об истечении Telegram сессии (вызывается только один раз)
 */
function notifySessionExpired(): void {
  if (!sessionExpiredNotified && onSessionExpiredCallback) {
    sessionExpiredNotified = true;
    onSessionExpiredCallback();
  }
}

/**
 * Сбросить флаг уведомления (вызывать после успешной авторизации в Telegram)
 */
export function resetSessionExpiredFlag(): void {
  sessionExpiredNotified = false;
}

/**
 * Проверить, является ли ошибка FILE_REFERENCE_EXPIRED
 * Бэкенд автоматически пытается repair, но при повторной ошибке возвращает этот код
 */
function isFileReferenceError(errorData: { errors?: string[]; code?: string }): boolean {
  if (errorData.code === 'FILE_REFERENCE_EXPIRED') {
    return true;
  }
  return errorData.errors?.some((e: string) =>
    e.includes('FILE_REFERENCE')
  ) ?? false;
}

/**
 * Проверить, является ли ответ ошибкой авторизации Telegram сессии
 * Бэкенд возвращает HTTP 400 с ошибками типа SESSION_EXPIRED, AUTH_KEY_UNREGISTERED
 *
 * НЕ путать с HTTP 401 — это ошибка JWT, которая обрабатывается глобально
 */
function isTelegramSessionError(errorData: { errors?: string[]; code?: string }): boolean {
  // Проверяем код ошибки
  if (errorData.code) {
    const sessionErrorCodes = [
      'SESSION_EXPIRED',
      'AUTH_KEY_UNREGISTERED',
      'AUTH_KEY_INVALID',
      'AUTH_KEY_PERM_EMPTY',
      'SESSION_REVOKED',
      'USER_DEACTIVATED',
    ];
    if (sessionErrorCodes.includes(errorData.code)) {
      return true;
    }
  }

  // Fallback: проверяем текстовые сообщения
  return errorData.errors?.some((e: string) =>
    e.includes('не авторизован') ||
    e.includes('сессия') ||
    e.includes('session') ||
    e.includes('SESSION_EXPIRED') ||
    e.includes('AUTH_KEY')
  ) ?? false;
}

/**
 * Хук для загрузки медиа с авторизацией
 * Возвращает blob URL, который можно использовать в img src
 */
export function useMediaUrl(chatId: number | null, messageId: number | null): {
  url: string | null;
  isLoading: boolean;
  error: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!chatId || !messageId) {
      setUrl(null);
      return;
    }

    // Если уже уведомили об истечении сессии - не делаем запросы
    if (sessionExpiredNotified) {
      setError(true);
      return;
    }

    const cacheKey = `${chatId}-${messageId}`;

    // Проверяем кэш
    const cached = blobUrlCache.get(cacheKey);
    if (cached) {
      setUrl(cached);
      return;
    }

    const fetchMedia = async (isRetry = false) => {
      setIsLoading(true);
      setError(false);

      try {
        // Authorization header добавляется автоматически глобальным интерцептором
        // 401 ошибки (JWT) также обрабатываются автоматически с refresh токена
        const response = await fetch(
          `${API_URL}/api/telegram-client/chats/${chatId}/messages/${messageId}/media`
        );

        if (!response.ok) {
          // Проверяем, ошибка ли это Telegram сессии (HTTP 400, не 401)
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              // FILE_REFERENCE_EXPIRED — retry один раз
              // Бэкенд уже пытается repair, но второй запрос получит свежий file reference
              if (!isRetry && isFileReferenceError(errorData)) {
                setIsLoading(false);
                await fetchMedia(true);
                return;
              }
              if (isTelegramSessionError(errorData)) {
                notifySessionExpired();
              }
            } catch {
              // игнорируем ошибку парсинга
            }
          }
          throw new Error('Не удалось загрузить медиа');
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Кэшируем blob URL
        blobUrlCache.set(cacheKey, blobUrl);
        setUrl(blobUrl);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, [chatId, messageId]);

  return { url, isLoading, error };
}

/**
 * Хук для загрузки аватарки с авторизацией
 */
export function useAvatarUrl(chatId: number | null): {
  url: string | null;
  isLoading: boolean;
  error: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!chatId) {
      setUrl(null);
      return;
    }

    // Если уже уведомили об истечении сессии - не делаем запросы
    if (sessionExpiredNotified) {
      setError(true);
      return;
    }

    const cacheKey = `avatar-${chatId}`;

    // Проверяем кэш
    const cached = blobUrlCache.get(cacheKey);
    if (cached) {
      setUrl(cached);
      return;
    }

    const fetchAvatar = async () => {
      setIsLoading(true);
      setError(false);

      try {
        // Authorization header добавляется автоматически глобальным интерцептором
        const response = await fetch(
          `${API_URL}/api/telegram-client/chats/${chatId}/avatar`
        );

        if (!response.ok) {
          // Проверяем, ошибка ли это Telegram сессии (HTTP 400)
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              if (isTelegramSessionError(errorData)) {
                notifySessionExpired();
              }
            } catch {
              // игнорируем ошибку парсинга
            }
          }
          throw new Error('Не удалось загрузить аватар');
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Кэшируем blob URL
        blobUrlCache.set(cacheKey, blobUrl);
        setUrl(blobUrl);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatar();
  }, [chatId]);

  return { url, isLoading, error };
}

/**
 * Хук для загрузки аватарки пользователя по Telegram ID
 * Используется для отображения аватарок в реакциях
 */
export function useUserAvatar(telegramUserId: number | null): {
  url: string | null;
  isLoading: boolean;
  error: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!telegramUserId) {
      setUrl(null);
      return;
    }

    // Если уже уведомили об истечении сессии - не делаем запросы
    if (sessionExpiredNotified) {
      setError(true);
      return;
    }

    const cacheKey = `user-avatar-${telegramUserId}`;

    // Проверяем кэш
    const cached = blobUrlCache.get(cacheKey);
    if (cached) {
      setUrl(cached);
      return;
    }

    const fetchAvatar = async () => {
      setIsLoading(true);
      setError(false);

      try {
        // Authorization header добавляется автоматически глобальным интерцептором
        const response = await fetch(
          `${API_URL}/api/telegram-client/users/${telegramUserId}/avatar`
        );

        if (!response.ok) {
          // Проверяем, ошибка ли это Telegram сессии (HTTP 400)
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              if (isTelegramSessionError(errorData)) {
                notifySessionExpired();
              }
            } catch {
              // игнорируем ошибку парсинга
            }
          }
          throw new Error('Не удалось загрузить аватар');
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Кэшируем blob URL
        blobUrlCache.set(cacheKey, blobUrl);
        setUrl(blobUrl);
      } catch (_error) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatar();
  }, [telegramUserId]);

  return { url, isLoading, error };
}

/**
 * Функция для загрузки медиа напрямую (для скачивания файлов)
 */
export async function fetchMediaWithAuth(chatId: number, messageId: number): Promise<Blob | null> {
  // Если уже уведомили об истечении Telegram сессии - не делаем запросы
  if (sessionExpiredNotified) {
    return null;
  }

  try {
    // Authorization header добавляется автоматически глобальным интерцептором
    const response = await fetch(
      `${API_URL}/api/telegram-client/chats/${chatId}/messages/${messageId}/media`
    );

    if (!response.ok) {
      // Проверяем, ошибка ли это Telegram сессии (HTTP 400)
      if (response.status === 400) {
        try {
          const errorData = await response.json();
          if (isTelegramSessionError(errorData)) {
            notifySessionExpired();
          }
        } catch {
          // игнорируем ошибку парсинга
        }
      }
      return null;
    }

    return response.blob();
  } catch {
    return null;
  }
}

/**
 * Очистка кэша blob URL при размонтировании компонентов
 * Вызывать при выходе со страницы telegram-client
 */
export function clearMediaCache(): void {
  blobUrlCache.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  blobUrlCache.clear();
}

/**
 * Хук для предзагрузки медиа сообщения
 * Возвращает функцию для загрузки и кэширования медиа
 */
export function usePreloadMedia(): (chatId: number, messageId: number) => Promise<string | null> {
  return useCallback(async (chatId: number, messageId: number) => {
    // Если уже уведомили об истечении Telegram сессии - не делаем запросы
    if (sessionExpiredNotified) {
      return null;
    }

    const cacheKey = `${chatId}-${messageId}`;

    // Проверяем кэш
    const cached = blobUrlCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Authorization header добавляется автоматически глобальным интерцептором
      const response = await fetch(
        `${API_URL}/api/telegram-client/chats/${chatId}/messages/${messageId}/media`
      );

      if (!response.ok) {
        // Проверяем, ошибка ли это Telegram сессии (HTTP 400)
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            if (isTelegramSessionError(errorData)) {
              notifySessionExpired();
            }
          } catch {
            // игнорируем ошибку парсинга
          }
        }
        return null;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      blobUrlCache.set(cacheKey, blobUrl);
      return blobUrl;
    } catch {
      return null;
    }
  }, []);
}
